// 健康面板（管理员总览）：3 张统计卡（点卡片筛选）+「需要优先处理」（有告警的类型）
// + 列定义驱动的类型表（成熟度、启用开关、通过率等指标、告警；点行进类型详情；「刚刚发生了什么」打开最近一次运行）
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorText } from "../../lib/api";
import { fmtPct } from "../../lib/format";
import { useAction, useLoad } from "../../lib/hooks";
import type { HealthItem } from "../../lib/types";
import { Badge, MaturityBadge } from "../../components/Badges";
import { ColumnPicker, DataTable, useHiddenColumns, type Column } from "../../components/DataTable";
import { SubTabs, TYPE_TABS } from "../../components/SubTabs";
import { PageHeading } from "../../components/PageHeading";
import { useAlerts } from "../../lib/alerts";

type Filter = "all" | "enabled" | "alerts";
const FILTER_TEXT: Record<Filter, string> = { all: "全部", enabled: "已启用", alerts: "有告警" };

export function HealthPage() {
  const navigate = useNavigate();
  const health = useLoad(() => api.health(), []);
  const act = useAction();
  const { toast } = useAlerts();
  const [filter, setFilter] = useState<Filter>("all");
  const [hidden, toggleCol] = useHiddenColumns("csp-hidden-columns-health");

  const toggle = async (typeId: string, enabled: boolean) => {
    const r = await act.run(`en-${typeId}`, () => api.updateType(typeId, { enabled }));
    if (r) void health.reload();
  };
  const latest = async (typeId: string) => {
    try {
      const r = await api.latestRun(typeId);
      if (r) navigate(`/tasks/${r.taskId}`);
      else toast("info", "这个类型还没有运行过");
    } catch (e) {
      toast("error", errorText(e));
    }
  };

  const all = health.data ?? [];
  const withAlerts = all.filter((h) => h.alerts.length > 0);
  const rows = useMemo(
    () => (filter === "enabled" ? all.filter((h) => h.enabled) : filter === "alerts" ? all.filter((h) => h.alerts.length > 0) : all),
    [all, filter],
  );
  const pick = (f: Filter) => setFilter(filter === f ? "all" : f);

  const columns: Column<HealthItem>[] = [
    {
      key: "name",
      label: "任务类型",
      fixed: true,
      render: (h) => (
        <div className="cell-two">
          <b title={h.name}>
            <Link to={`/admin/types/${h.typeId}`} className="plain" onClick={(e) => e.stopPropagation()}>
              {h.name}
            </Link>
          </b>
          <span>{h.slug}</span>
        </div>
      ),
    },
    { key: "maturity", label: "成熟度", fixed: true, render: (h) => <MaturityBadge maturity={h.maturity} /> },
    {
      key: "enabled",
      label: "启用",
      render: (h) => (
        <label className="check" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={h.enabled}
            disabled={!!act.busy}
            aria-label={`启用 ${h.name}`}
            onChange={(e) => void toggle(h.typeId, e.target.checked)}
          />
          {h.enabled ? <Badge tone="green">已启用</Badge> : <Badge tone="gray">未启用</Badge>}
        </label>
      ),
    },
    { key: "sop", label: "SOP 版本", render: (h) => <span className="mono">v{h.skillVersion}</span> },
    { key: "pass", label: "30 天通过率", render: (h) => fmtPct(h.stats.passRate30d) },
    { key: "unexpected", label: "意外干预率", render: (h) => fmtPct(h.stats.unexpectedInterventionRate) },
    { key: "avg", label: "平均分钟", render: (h) => h.stats.avgMinutes ?? <span className="muted">—</span> },
    { key: "red", label: "红字段率", render: (h) => fmtPct(h.stats.redFieldRate) },
    { key: "runs", label: "30 天运行", render: (h) => h.stats.runs30d },
    {
      key: "alerts",
      label: "告警",
      tone: (h) => (h.alerts.length ? "cell-bad" : undefined),
      render: (h) =>
        h.alerts.length ? (
          <span className="cell-clip" title={h.alerts.join("\n")}>
            ⚠ {h.alerts.join("；")}
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: "op",
      label: "操作",
      fixed: true,
      render: (h) => (
        <button
          className="btn btn-small"
          onClick={(e) => {
            e.stopPropagation();
            void latest(h.typeId);
          }}
        >
          刚刚发生了什么
        </button>
      ),
    },
  ];

  const STATS: { key: Filter; label: string; n: number; tone: string }[] = [
    { key: "all", label: "任务类型", n: all.length, tone: "" },
    { key: "enabled", label: "已启用", n: all.filter((h) => h.enabled).length, tone: "" },
    { key: "alerts", label: "有告警", n: withAlerts.length, tone: "danger" },
  ];

  return (
    <div>
      <PageHeading title="健康面板" desc="每个任务类型最近 30 天跑得怎么样；点一行进类型详情改 SOP / 看版本 / 看时间线">
        <button
          className="btn"
          onClick={() => void health.reload()}
        >
          刷新数据
        </button>
      </PageHeading>
      <SubTabs tabs={TYPE_TABS} />

      <div className="stat-cards" data-testid="stat-cards">
        {STATS.map((c) => {
          const active = c.key !== "all" && filter === c.key;
          return (
            <button
              key={c.key}
              type="button"
              className={`stat-card ${c.n ? c.tone : "zero"}${active ? " active" : ""}`}
              onClick={() => pick(c.key)}
              aria-pressed={active}
              data-stat={c.key}
            >
              <span className="num">{c.n}</span>
              <span className="lbl">{c.label}</span>
            </button>
          );
        })}
      </div>

      {withAlerts.length > 0 && (
        <div className="card" data-testid="urgent-list">
          <h2>
            需要优先处理 <span className="tab-count">{withAlerts.length}</span>
          </h2>
          {withAlerts.map((h) => (
            <Link key={h.typeId} to={`/admin/types/${h.typeId}`} className="urgent">
              <MaturityBadge maturity={h.maturity} />
              <span className="what">
                <b>{h.name}</b>
                <span className="muted small"> · {h.slug}</span>
                <span className="small reason">⚠ {h.alerts.join("；")}</span>
              </span>
              <span className="btn btn-small">去看看 →</span>
            </Link>
          ))}
        </div>
      )}

      <div className="card">
        {filter !== "all" && (
          <div className="current-filter" data-testid="current-filter">
            当前筛选：{FILTER_TEXT[filter]}
            <button type="button" className="btn btn-link small" onClick={() => setFilter("all")}>
              清除
            </button>
          </div>
        )}
        <div className="list-bar">
          <span className="count">
            共 {rows.length} 个类型 · 按英文标识（slug）排序 · 有告警的格标红底
          </span>
          <ColumnPicker columns={columns} hidden={hidden} onToggle={toggleCol} />
        </div>
        {act.error && <div className="notice err">{act.error}</div>}
        {health.error && <div className="notice err">{health.error}</div>}
        {health.loading && !health.data ? (
          <div className="empty">正在加载…</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(h) => h.typeId}
            hidden={hidden}
            onRowClick={(h) => navigate(`/admin/types/${h.typeId}`)}
            rowAttrs={(h) => ({ "data-testid": "health-row", "data-slug": h.slug })}
            empty={filter === "all" ? "还没有任务类型" : `没有「${FILTER_TEXT[filter]}」的类型`}
          />
        )}
      </div>
    </div>
  );
}
